import { type Copy } from '@/james-playground/aa-family-claim/claim/asks';
import {
  type HelpKind,
  type StayLine,
} from '@/james-playground/aa-family-claim/claim/cards/values';

/**
 * Copy for the answer cards. Each line about her has a "you" form for a
 * claimant using the app herself, resolved with `say()`. A plain string is
 * turned into the "you" form by `say()`'s own rules; a pair is used where those
 * rules would trip ("herself", "she’s").
 *
 * Replies are the family's own words in the conversation, so their "you" form
 * is first person: "No, I manage this on my own."
 *
 * Questions the form asks are reworded plainly here. The form's own wording is
 * in the registry and goes on the PDF untouched.
 */

export const CARD = {
  continue: 'Continue',
  skip: 'I’m not sure, we’ll talk it through',
  yes: 'Yes',
  no: 'No',
  fewer: 'Fewer',
  more: 'More',
  choose: 'Choose',
  remove: 'Remove',
  somethingElse: 'Something else',
  optional: 'Optional',
  formats: {
    date: 'Enter a date like 19/03/1942.',
    ni: 'Enter 2 letters, 6 numbers and a letter, like QQ 12 34 56 C.',
    phone: 'Enter a phone number with 10 or 11 digits, like 0117 496 0000.',
  },
  datePlaceholder: 'DD/MM/YYYY',
};

/** The Yes/No question that opens each gated ask. */
export const GATE: Record<string, Copy> = {
  bedNeedsHelp: 'Does she struggle or need help getting into or out of bed?',
  toiletNeedsHelp:
    'Does she struggle or need help with the toilet or continence in the day?',
  washingNeedsHelp:
    'Does she struggle or need help with washing, bathing or her appearance?',
  dressingNeedsHelp:
    'Does she struggle or need help getting dressed or undressed?',
  movingIndoorsNeedsHelp:
    'Does she struggle or need help moving around indoors?',
  eatingNeedsHelp:
    'Does she struggle or need help with eating, drinking or cutting up food?',
  medicationNeedsHelp:
    'Does she struggle or need help with medicines or treatment?',
  communicationNeedsHelp: {
    she: 'Does she need someone to help her communicate with other people?',
    you: 'Do you need someone to help you communicate with other people?',
  },
  activityNeedsHelp: {
    she: 'Does she need someone with her to take part in hobbies, social or religious activities?',
    you: 'Do you need someone with you to take part in hobbies, social or religious activities?',
  },
  daySupervisionNeeded:
    'Does someone need to keep an eye on her in the day to keep her safe?',
  nightNeedsHelp: 'Does she need help during the night?',
  nightSupervisionNeeded:
    'Does someone need to stay awake to watch over her at night?',
  seenProfessional: {
    she: 'Apart from her GP, has she seen anyone about her conditions in the last 12 months?',
    you: 'Apart from your GP, have you seen anyone about your conditions in the last 12 months?',
  },
  hasHelper: {
    she: 'Does anyone help her because of her conditions?',
    you: 'Does anyone help you because of your conditions?',
  },
  hasReports:
    'Does she have any reports about her conditions, like a letter from a consultant or an assessment?',
  surgeryWaitingList: 'Is she on a waiting list for an operation?',
  hadTests:
    'Has she had any tests for her conditions, like a hearing or sight test?',
  currentStay: 'Is she in a hospital, hospice or care home now?',
  previousStayLastSixWeeks:
    'Has she left a hospital, hospice or care home in the last 6 weeks?',
  supportingStatementWanted: {
    she: 'Is there someone who knows her well who could write a few lines about her?',
    you: 'Is there someone who knows you well who could write a few lines about you?',
  },
};

/** Asks whose opening question is the gate itself, so the card does not repeat it. */
export const GATE_IN_PROMPT = new Set([
  'stays-now',
  'stays-recent',
  'safe-watch',
  'night-watch',
  'day-activities',
  'health-professionals',
]);

/** Under a care gate: the form counts the help she needs, given or not. */
export const NEEDED_HINT: Copy = {
  she: 'Count the help she needs, even if nobody gives it yet.',
  you: 'Count the help you need, even if nobody gives it yet.',
};

/** A No on a care gate, as the family's reply. */
export const MANAGES: Copy = {
  she: 'No, she manages this on her own',
  you: 'No, I manage this on my own',
};

// ---- Help grids ------------------------------------------------------------

export const HELP_KIND: Record<HelpKind, Copy> = {
  difficulty: { she: 'Struggles with it', you: 'Struggle with it' },
  help: { she: 'Needs hands-on help', you: 'Need hands-on help' },
  prompt: {
    she: 'Needs reminding or encouraging',
    you: 'Need reminding or encouraging',
  },
};

export const GRID = {
  pick: {
    she: 'Tap everything that applies.',
    you: 'Tap everything that applies.',
  } as Copy,
  timesADay: 'times a day',
  timesLabel: (kind: string, row: string) =>
    `${row}, ${kind.toLowerCase()}: times a day`,
};

// ---- Night -----------------------------------------------------------------

export const NIGHT = {
  pick: 'Tick each thing she needs help with at night.',
  times: 'Times a night',
  minutes: 'Minutes each time',
  minutesUnit: 'minutes',
  /** The form's own 1, 2 and 3+. */
  timesOption: { '1': 'Once', '2': 'Twice', '3+': '3 or more' } as Record<
    string,
    string
  >,
  timesReply: {
    '1': 'once a night',
    '2': 'twice a night',
    '3+': '3 or more times a night',
  } as Record<string, string>,
};

// ---- Keeping her safe -------------------------------------------------------

/** The form's reasons, said plainly. Keyed by the end of the entry id. */
export const REASON: Record<string, Copy> = {
  Danger: {
    she: 'She could put herself or other people in danger',
    you: 'You could put yourself or other people in danger',
  },
  UnawareOfDanger: {
    she: 'She does not notice everyday dangers, like a hot pan or a busy road',
    you: 'You do not notice everyday dangers, like a hot pan or a busy road',
  },
  SelfNeglect: {
    she: 'She might not look after herself, like eating or keeping warm',
    you: 'You might not look after yourself, like eating or keeping warm',
  },
  SelfHarm: {
    she: 'She is at risk of harming herself',
    you: 'You are at risk of harming yourself',
  },
  Wandering: { she: 'She may wander off', you: 'You may wander off' },
  Behaviour: {
    she: 'She can become aggressive or upset other people',
    you: 'You can become aggressive or upset other people',
  },
  Fits: {
    she: 'She has fits, dizzy spells or blackouts',
    you: 'You have fits, dizzy spells or blackouts',
  },
  Confusion: { she: 'She gets confused', you: 'You get confused' },
  Voices: {
    she: 'She hears voices or has thoughts that get in the way of her thinking',
    you: 'You hear voices or have thoughts that get in the way of your thinking',
  },
};

/** The reasons as short names, for the reply. */
export const REASON_SHORT: Record<string, string> = {
  Danger: 'danger to self or others',
  UnawareOfDanger: 'not noticing dangers',
  SelfNeglect: 'risk of self-neglect',
  SelfHarm: 'risk of self-harm',
  Wandering: 'wandering',
  Behaviour: 'aggressive behaviour',
  Fits: 'fits or blackouts',
  Confusion: 'confusion',
  Voices: 'hearing voices',
};

export const WATCH = {
  reasons: {
    day: 'Why does someone need to keep an eye on her?',
    night: 'Why does someone need to watch over her at night?',
  } as Record<'day' | 'night', Copy>,
  aloneFor: {
    she: 'How long can she safely be left on her own?',
    you: 'How long can you safely be left on your own?',
  } as Copy,
  aloneChips: ['Not at all', 'A few minutes', 'Up to an hour', 'A few hours'],
  cannotBeLeft: {
    she: 'She can’t be left on her own',
    you: 'I can’t be left on my own',
  } as Copy,
  aloneField: 'How long',
  times: 'How many times a night does someone need to be awake for her?',
  timesUnit: 'times a night',
  howLong: 'How long each time, roughly?',
  howLongChips: [
    'A few minutes',
    'About 15 minutes',
    'About 30 minutes',
    'An hour or more',
  ],
  howLongField: 'How long each time',
};

// ---- Falls -----------------------------------------------------------------

export const FALLS = {
  options: [
    { value: 'Fall', label: 'Falls' },
    { value: 'Stumble', label: 'Stumbles or nearly falls' },
    { value: 'No', label: 'Neither' },
  ] as const,
  fallsHeading: 'Falls',
  stumblesHeading: 'Stumbles',
  lastFall: 'When was the last fall?',
  lastStumble: 'When was the last stumble?',
  lastMonth: 'In the last month',
  lastYear: 'In the last year',
  fallsUnit: 'falls',
  stumblesUnit: 'stumbles',
  needsHelp: 'Does she need help to get up after a fall?',
  gettingUp: 'What help does she need to get up?',
  clinic: 'Has she been referred to a falls clinic?',
  whatHappens: {
    she: 'What happens when she falls or stumbles?',
    you: 'What happens when you fall or stumble?',
  } as Copy,
  whatHappensHint: 'Why it happens, and whether she gets hurt.',
};

// ---- Hobbies and getting out ------------------------------------------------

export const ACTIVITIES = {
  home: 'At home',
  outside: 'Out and about',
  homeExample: 'For example reading, a hobby, video calls with family',
  outsideExample: 'For example church, a club, seeing friends',
  columns: {
    what: {
      she: 'What she does, or would like to do',
      you: 'What you do, or would like to do',
    } as Copy,
    help: 'The help she needs from someone',
    often: 'How often',
  },
  add: 'Add another',
  row: (n: number) => `Activity ${n}`,
  removeRow: (n: number) => `Remove activity ${n}`,
};

// ---- Days and nights a week ------------------------------------------------

export const AMOUNT = {
  days: 'days a week',
  nights: 'nights a week',
  every: { days: 'Every day', nights: 'Every night' },
  one: { days: 'day a week', nights: 'night a week' },
};

// ---- Rough dates -------------------------------------------------------------

export const APPROX = {
  month: 'Month',
  year: 'Year',
  noMonth: 'I’m not sure of the month',
  waiting: (from: string) => ({
    she: `Payments could start from ${from}, once she has needed help for 6 months. You can still send the claim now.`,
    you: `Payments could start from ${from}, once you have needed help for 6 months. You can still send the claim now.`,
  }),
};

// ---- Choices -----------------------------------------------------------------

/** The plain question for an entry inside a card holding several. */
export const QUESTION: Record<string, Copy> = {
  existingDisabilityAward: 'Does she get any of these now?',
  nationality: 'What is her nationality?',
  eeaBefore2021: {
    she: 'Was she living in the UK before 1 January 2021?',
    you: 'Were you living in the UK before 1 January 2021?',
  },
  accommodation: 'What sort of home does she live in?',
  toiletLocation: 'Where is the toilet?',
  toiletOtherLocation: 'Where is the toilet?',
  sleepLocation: 'Where does she sleep?',
  sleepOtherLocation: 'Where does she sleep?',
  constantAttendance: {
    she: 'Does she get either of these, or is she waiting to hear about one?',
    you: 'Do you get either of these, or are you waiting to hear about one?',
  },
  normallyLiveGb: 'Does she normally live in Great Britain?',
  welshContact: 'Would she like DWP to write to her in Welsh?',
  abroadOverFourWeeks:
    'Has she been abroad for more than 4 weeks at a time in the last 3 years?',
  eeaBenefits:
    'Does she or her partner get a pension or benefit from an EEA country or Switzerland?',
  eeaWorkInsurance:
    'Does she or her partner work or pay insurance in an EEA country or Switzerland?',
  movedFromScotland: 'Has she moved to England or Wales from Scotland?',
  padpClaim: 'Has she claimed Pension Age Disability Payment?',
  publiclyFundedStay:
    'Does a council, the NHS or another public body pay for any part of the stay?',
  currentStayPlaceType: 'Where is she staying?',
  previousStayPlaceType: 'Where did she stay?',
  bankAccountHasRollNumber: 'Does the account have a roll number?',
};

/** How an option reads on screen, where the registry's label is the form's. */
export const OPTION_LABEL: Record<string, Record<string, Copy>> = {
  existingDisabilityAward: {
    'PIP, DLA or Adult Disability Payment':
      'PIP, Disability Living Allowance or Adult Disability Payment',
    'PIP or Adult Disability Payment that ended in the last 12 months':
      'PIP or Adult Disability Payment that stopped in the last 12 months',
  },
  constantAttendance: {
    'War Pension Constant Attendance Allowance':
      'Constant Attendance Allowance with a War Pension',
    'Industrial Injuries Disablement Benefit Constant Attendance Allowance':
      'Constant Attendance Allowance with Industrial Injuries Disablement Benefit',
    'Neither (both boxes left blank)': 'Neither',
  },
  eeaBefore2021: { 'Do not know': 'I don’t know' },
  eeaBenefits: { 'Do not know': 'I don’t know' },
  eeaWorkInsurance: { 'Do not know': 'I don’t know' },
};

/** Free-text answers with the common ones offered as chips. */
export const SUGGEST: Record<
  string,
  { chips: string[]; other: string; field: Copy }
> = {
  nationality: {
    chips: ['British', 'Irish'],
    other: 'Other',
    field: { she: 'Her nationality', you: 'Your nationality' },
  },
  accommodation: {
    chips: ['House', 'Bungalow', 'Flat', 'Care home'],
    other: 'Somewhere else',
    field: 'What sort of home',
  },
  supporterRelationship: {
    chips: ['Friend', 'Neighbour', 'Family member', 'GP', 'Nurse', 'Carer'],
    other: 'Someone else',
    field: 'How they know her',
  },
  supporterFrequency: {
    chips: ['Every day', 'Most days', 'Once a week', 'Less often'],
    other: 'Something else',
    field: 'How often they see her',
  },
};

// ---- Typed fields ------------------------------------------------------------

/** Field labels, where the short name in labels.ts would read oddly as one. */
export const FIELD: Record<string, Copy> = {
  surname: 'Surname',
  firstNames: 'First names',
  otherNames: 'Other names, if any',
  title: 'Title, like Mrs or Mr',
  dob: 'Date of birth',
  address: 'Address',
  postcode: 'Postcode',
  niNumber: 'National Insurance number',
  mobile: 'Mobile number',
  phone: 'Daytime number, if different',
  abroadFrom: 'Date she left',
  abroadTo: 'Date she came back',
  abroadWhere: 'Where she went',
  abroadReason: 'Why she went',
  scotlandMoveDate: 'Date she moved to England or Wales',
  gpName: 'GP or surgery name',
  gpAddress: 'Surgery address',
  gpPostcode: 'Postcode',
  gpPhone: 'Surgery phone number',
  gpLastSeen: 'When she last saw them about her conditions',
  currentStayFrom: 'Date she went in',
  previousStayFrom: 'Date she went in',
  previousStayTo: 'Date she came out',
  currentStayName: 'Name of the place',
  previousStayName: 'Name of the place',
  currentStayAddress: 'Street address',
  previousStayAddress: 'Street address',
  currentStayTown: 'Town or city',
  previousStayTown: 'Town or city',
  currentStayPostcode: 'Postcode',
  previousStayPostcode: 'Postcode',
  currentStayReason: 'Why she went into hospital',
  previousStayReason: 'Why she went into hospital',
  fundingAuthority: 'Which council, NHS body or department pays',
  declarationPrintedName: 'Name of the person signing',
  toiletOtherLocation: 'Where the toilet is',
  sleepOtherLocation: 'Where she sleeps',
  representativeOrganisation: 'Name of the organisation',
  representativeName: 'Your full name',
  representativeNiNumber: 'Your National Insurance number',
  representativeDob: 'Your date of birth',
  representativeAddress: 'Your address',
  representativePostcode: 'Postcode',
  representativePhone: 'Your daytime phone number',
  supporterName: 'Their name',
  supporterAddress: 'Their address',
  supporterPostcode: 'Postcode',
  supporterPhone: 'Their daytime phone number',
  bankRollNumber: 'Roll or reference number',
};

export const HINT: Record<string, Copy> = {
  niNumber: 'It’s on letters about her State Pension.',
  phone: 'Leave this blank if it’s the same as the mobile.',
  representativeNiNumber: 'Optional if you sign as part of your job.',
  representativeDob: 'Optional if you sign as part of your job.',
};

export const PLACEHOLDER: Record<string, string> = {
  niNumber: 'QQ 12 34 56 C',
  mobile: '07700 900000',
  phone: '0117 496 0000',
  gpPhone: '0117 496 0000',
};

// ---- Tables ------------------------------------------------------------------

/** What one row of each table is called. */
export const ROW_NOUN: Record<string, { one: string; add: string }> = {
  illnessesTable: { one: 'Condition', add: 'Add another condition' },
  aidsTable: { one: 'Aid', add: 'Add another aid' },
  professionalsTable: { one: 'Professional', add: 'Add another' },
  helpersTable: { one: 'Person', add: 'Add another person' },
  surgeryWaitingListTable: { one: 'Operation', add: 'Add another operation' },
  testsTable: { one: 'Test', add: 'Add another test' },
};

export const TABLE = {
  row: (noun: string, n: number, of: number) =>
    of > 1 ? `${noun} ${n}` : noun,
  remove: (noun: string, n: number) => `Remove ${noun.toLowerCase()} ${n}`,
  full: (n: number) =>
    n === 1
      ? 'The form has room for one. Add anyone else under Anything else.'
      : `The form has room for ${n}.`,
  none: {
    aidsTable: {
      she: 'She doesn’t use anything like this',
      you: 'I don’t use anything like this',
    } as Copy,
  } as Record<string, Copy>,
  prescribed:
    'Prescribed by a health professional, like an occupational therapist',
};

export const COLUMN: Record<string, Copy> = {
  conditions: 'Condition or disability',
  conditionsDuration: 'How long she has had it',
  medications: 'Medicines or treatment for it',
  medicationDosage: 'Dose and how often',
  aidName: 'Aid or adaptation',
  aidBenefit: 'How it helps her',
  aidDifficulty: 'Any difficulty using it',
  professionalName: 'Their title and name',
  professionalRole: 'Their job or specialty',
  professionalAddress: 'Where she sees them',
  professionalPostcode: 'Postcode',
  professionalPhone: 'Their phone number',
  hospitalRecordNumber: 'Her hospital number, if she has one',
  professionalConditions: 'Which conditions they see her for',
  professionalFrequency: 'How often she sees them',
  professionalLastSeen: 'When she last saw them',
  helperName: 'Their name',
  helperRelationship: 'How they know her',
  helperSupport: 'What they help with',
  helperFrequency: 'How often they help',
  helperAddress: 'Their address',
  helperPostcode: 'Postcode',
  helperPhone: 'Their phone number',
  surgeryType: 'What the operation is',
  surgeryWaitDate: 'Date she went on the list',
  surgeryDate: 'Date it is planned for, if known',
  testTypeAndDate: 'The test and when it was',
  testResult: 'The result',
};

// ---- Hospital and care stays -------------------------------------------------

export const STAY = {
  placeTypes: {
    Hospital: 'Hospital',
    Hospice: 'Hospice',
    'Care home': 'Care home',
  } as Record<string, string>,
  details: 'About the stay',
  line: {
    hospital: {
      she: 'Attendance Allowance is not paid while someone is in hospital. If it is awarded, it starts from the first payday after she leaves.',
      you: 'Attendance Allowance is not paid while someone is in hospital. If it is awarded, it starts from the first payday after you leave.',
    },
    publicCareHome:
      'Attendance Allowance is not usually paid while a council or the NHS pays for the care home. It’s still worth claiming so the award is in place.',
    publicHospice:
      'Attendance Allowance is not usually paid while the NHS or a council pays for the hospice. It’s still worth claiming so the award is in place.',
    canBePaid: 'Attendance Allowance can be paid.',
    hospiceQuicker:
      'Attendance Allowance can be paid while she is in the hospice.',
  } satisfies Record<StayLine, Copy>,
  carers: {
    she: 'If someone gets Carer’s Allowance for looking after her, that can stop too.',
    you: 'If someone gets Carer’s Allowance for looking after you, that can stop too.',
  } as Copy,
  title: 'What this means for payment',
};

// ---- Bank ------------------------------------------------------------------------

export const BANK_CARD = {
  secure:
    'Bank details go in a secure step of their own. They never appear in the conversation.',
  add: 'Add the account details',
  change: 'Change the account details',
  sheetTitle: 'Where should Attendance Allowance be paid?',
  body: {
    she: 'Use an account in her name, or a joint account she can use.',
    you: 'Use an account in your name, or a joint account.',
  } as Copy,
  demo: 'This is a demo. Please use made-up details: nothing you type here is kept or sent.',
  holder: 'Name on the account',
  bank: 'Bank or building society',
  sortCode: 'Sort code',
  sortCodeHint: '6 numbers, like 00-00-00',
  account: 'Account number',
  accountHint: 'Usually 8 numbers',
  rollHint:
    'Some building society accounts have one. It’s on the statement or passbook.',
  save: 'Save the account details',
  invalidHolder: 'Enter the name on the account.',
  invalidBank: 'Enter the bank or building society.',
  invalidSortCode: 'Enter a 6-digit sort code, like 00-00-00.',
  invalidAccount: 'Enter the account number, usually 8 digits.',
  invalidRoll: 'Enter the roll number, or choose No.',
  saved: 'Account details saved',
  sortEnding: (last: string) => `Sort code ending ${last}`,
  accountEnding: (last: string) => `Account ending ${last}`,
  roll: 'Has a roll number',
  others: {
    she: 'Pay any other benefits she gets into this account too',
    you: 'Pay any other benefits you get into this account too',
  } as Copy,
  reply: 'Bank details added',
};

// ---- Who signs ---------------------------------------------------------------------

export const SIGNER = {
  who: {
    herself: {
      she: '{name} will sign it herself',
      you: 'I’ll sign it myself',
    } as Copy,
    quicker: 'I’ll sign it for her on the quicker route',
    attorney: 'I have Power of Attorney',
    authority: 'Someone else has legal authority',
    unsure: 'I’m not sure',
  },
  quickerNote:
    'On the quicker route you can sign for her without being appointed. DWP will write to {name} about Attendance Allowance. Their letters won’t mention the quicker route.',
  poaQuestion: 'What sort of Power of Attorney is it?',
  poa: {
    finance: 'Property and Financial Affairs, registered',
    welfare: 'Health and Welfare only',
    unregistered: 'It isn’t registered yet',
  },
  poaCannot: {
    welfare:
      'A Health and Welfare Power of Attorney covers decisions about care and treatment. To sign a benefit claim you need one for property and money.',
    unregistered:
      'A Power of Attorney has to be registered with the Office of the Public Guardian before it can be used.',
  },
  onlineQuestion: 'Do you have an online account for the Power of Attorney?',
  online: {
    Yes: 'DWP will contact you to ask for an access code.',
    No: 'Send the registered Power of Attorney with the claim, the original or a certified copy. DWP sends it back.',
  } as Record<string, string>,
  authorityQuestion: 'What sort of authority?',
  authority: {
    appointee: 'DWP appointee',
    deputy: 'Deputy, appointed by the Court of Protection',
    tutor: 'Tutor, under Scottish law',
    curator: 'Curator bonis or judicial factor, under Scottish law',
    organisation: 'An organisation, like a council or solicitors',
  },
  authorityNote: {
    appointee: 'Have the BF57 letter from DWP to hand.',
    deputy:
      'Send the court order with the claim, the original or a certified copy.',
    tutor:
      'Send the document giving the authority, the original or a certified copy.',
    curator:
      'Send the document giving the authority, the original or a certified copy.',
    organisation:
      'Send the document appointing the organisation, unless DWP has already seen it.',
  },
  explainer: [
    'Anyone in the family can fill in the form. Only certain people can sign it.',
    'If {name} can understand the form and sign it, she signs.',
    'If she can’t, you can apply to be appointed to act for her, and DWP will contact you.',
  ],
  unsure: {
    herself: 'She can sign',
    apply: 'I’ll apply to be appointed',
    adviser: 'Talk it through with my adviser',
  },
  details: 'Your details for the form',
  detailsNote: 'From your KareHero account. Check they’re right.',
  selfClaim: 'You’ll sign the form yourself.',
  reply: {
    herself: { she: '{name} will sign it', you: 'I’ll sign it myself' } as Copy,
    quicker: 'I’ll sign for her on the quicker route',
    attorney: 'I have Power of Attorney for her property and money',
    applying: 'I’ll apply to be appointed',
    appointee: 'I’m her DWP appointee',
    deputy: 'I’m her deputy',
    tutor: 'I’m her tutor',
    curator: 'I’m her curator bonis or judicial factor',
    organisation: 'An organisation signs for her',
  } as Record<string, Copy>,
};

// ---- One-off cards ---------------------------------------------------------------

export const CONSENT = {
  body: [
    'DWP may contact the people and organisations who know about her conditions, such as her GP, hospital or care workers, to understand her needs. It’s up to you.',
  ],
  yes: 'Yes, DWP can contact them',
  no: 'No',
  noNote: 'That’s allowed. It may take DWP longer to decide.',
  signs: 'The person signing the form signs this part too, once it’s printed.',
};

export const QUIET = {
  options: [
    { value: 'No', label: 'No' },
    { value: 'Yes', label: 'Yes' },
    { value: 'talk', label: 'I’d rather talk to an adviser first' },
  ] as const,
  yesNote:
    'Thank you for telling us. We’ll use the quicker route: there is less to fill in, and payments can start sooner.',
  talkNote:
    'Your Care Adviser will talk it through with you. We’ll carry on with the usual claim for now.',
};

export const START_DATE = {
  body: 'Ask the Attendance Allowance helpline for a claim form.',
  call: 'Ring 0800 731 0122',
  tel: 'tel:08007310122',
  hours: 'Monday to Friday, 8am to 6pm. Free to call.',
  rang: 'I’ve rung them',
  later: 'Later',
  laterReply: 'I’ll ring them later',
  rangReply: 'I’ve rung them today',
  already: (on: string) => `You rang DWP on ${on}.`,
  keep: 'That’s right',
};

export const SR1 = {
  body: 'It’s free, and they can fill it in without an appointment.',
  messageLabel: 'A message you can show or send them',
  message: (name: string, dob: string) =>
    `Please could you fill in an SR1 form for ${name}${dob ? `, date of birth ${dob}` : ''}? We’re claiming Attendance Allowance.`,
  copy: 'Copy the message',
  copied: 'Copied',
  asked: 'I’ve asked for it',
  later: 'I’ll ask later',
  askedReply: 'I’ve asked for an SR1',
  laterReply: 'I’ll ask for an SR1 later',
};

export const VARIABILITY = {
  options: {
    day: {
      'About the same every day': 'No, it’s about the same',
      'Worse on some days': 'Some days',
      'Worse on most days': 'Most days',
    },
    night: {
      'About the same every day': 'No, it’s about the same',
      'Worse on some days': 'Some nights',
      'Worse on most days': 'Most nights',
    },
  } as Record<'day' | 'night', Record<string, string>>,
  often: { day: 'How often is it worse?', night: 'How often is it worse?' },
  chips: {
    day: [
      'About one day a week',
      'Two or three days a week',
      'Most of the week',
    ],
    night: [
      'About one night a week',
      'Two or three nights a week',
      'Most of the week',
    ],
  },
  field: 'How often',
};

export const NARRATIVE = {
  drafted: 'Drafted from what you said',
  approve: 'Approve',
  approved: 'Approved',
  edit: 'Edit',
  done: 'Done',
  approveAll: 'Approve them all',
  editLabel: (box: string) => `The words for ${box}`,
  none: 'Nothing to draft yet. As you tell us more, the words for the form appear here.',
  extra: 'Anything else DWP should know',
  extraHint: 'Optional. Leave it blank if there’s nothing to add.',
  box: {
    bedHelp: 'Getting in and out of bed',
    toilet: 'The toilet',
    washing: 'Washing and bathing',
    dressing: 'Getting dressed',
    movingIndoors: 'Moving around indoors',
    eating: 'Eating and drinking',
    medicationHelp: 'Medicines and treatment',
    communication: 'Communicating',
    supervisionDay: {
      she: 'Keeping her safe in the day',
      you: 'Staying safe in the day',
    },
    nightHelp: 'Help at night',
    nightSupervision: 'Someone awake at night',
    variability: 'Good and bad days',
  } as Record<string, Copy>,
  reply: (n: number) =>
    n === 1
      ? 'Approved the words for 1 box'
      : `Approved the words for ${n} boxes`,
  nothing: 'Nothing to add',
};

export const SUPPORTING = {
  note: 'They can write their few lines on the printed form.',
  about: 'About them',
  sameAsSigner:
    'The person who signs the form can’t also write the supporting statement. Choose someone else who knows her well.',
  noReply: 'Not this time',
};

export const DOCUMENTS = {
  uploaded: 'Your uploads',
  route: 'For the way the form is signed',
  add: 'Add another document',
  addLabel: 'Document',
  none: 'We’re not sending any documents',
  routeDocs: {
    poa: 'The registered Power of Attorney, original or certified copy',
    deputy: 'The court order, original or certified copy',
    scottish: 'The document giving the authority, original or certified copy',
    organisation: 'The document appointing the organisation',
    appointee: 'A copy of the BF57 appointee letter',
    sr1: 'The SR1 form, if you have it',
  },
  reply: (n: number) =>
    n === 1 ? 'Sending 1 document' : `Sending ${n} documents`,
  noneReply: 'No documents',
};

export const BATCH = {
  intro: {
    she: 'Tick any she has difficulty with, or needs help, reminding or watching over with, even if nobody helps her yet.',
    you: 'Tick any you have difficulty with, or need help, reminding or watching over with, even if nobody helps you yet.',
  } as Copy,
  unticked: {
    she: 'We’ll tell DWP she manages the ones you leave unticked on her own.',
    you: 'We’ll tell DWP you manage the ones you leave unticked on your own.',
  } as Copy,
  reply: (names: string) => ({
    she: `She needs help with: ${names}. She manages the rest on her own.`,
    you: `I need help with: ${names}. I manage the rest on my own.`,
  }),
  replyNone: {
    she: 'She manages all of these on her own',
    you: 'I manage all of these on my own',
  } as Copy,
};

export const CONFIRM = {
  right: 'Yes, that’s right',
  change: 'Change something',
  from: (source: string) =>
    `From ${source.charAt(0).toLowerCase()}${source.slice(1)}`,
  none: 'None',
  notKnown: 'Not known yet',
  changed: 'Changed',
};
